/**
 * Shadow Breaker Dashboard Overlay — "Tactical Breach HUD" design
 * Unique layout: vertical price-ladder track (T3→T2→T1→Entry→SL→TSL) with
 * connecting rail + glowing node markers, pulsing signal ring, split
 * live/booked P&L readout. Distinct from the flat row-list layouts used by
 * other Premium Strategies overlays.
 * CSS prefix: sb-
 */
import React from "react";
import { ShadowBreakerDashboard } from "../../indicators/built-in/shadow-breaker";
import "./ShadowBreakerOverlay.scss";

interface ShadowBreakerOverlayProps {
  dashboard: ShadowBreakerDashboard | null;
  symbol: string;
  visible: boolean;
  showDashboard?: boolean;
  onToggleDashboard?: () => void;
  arrowBadges?: { x: number; y: number; direction: "long" | "short" }[];
}

const ShadowBreakerOverlay: React.FC<ShadowBreakerOverlayProps> = ({
  dashboard, symbol, visible,
  showDashboard = true, onToggleDashboard, arrowBadges = [],
}) => {
  if (!visible || !dashboard) return null;

  const { signal, trend, entry, stopLoss, trailingStop,
    target1, target2, target3, currentPrice, livePL, bookedPL,
    status, tp1Hit, tp2Hit, tp3Hit, slHit, tslHit } = dashboard;

  const isLong = trend === "LONG";
  const isShort = trend === "SHORT";
  const isActive = status === "Active";
  const fmt = (v: number | null) => (v !== null ? v.toFixed(2) : "—");
  const fmtPL = (v: number) => (v >= 0 ? `+${v.toFixed(2)}` : v.toFixed(2));

  const themeClass = isLong ? "sb-buy" : isShort ? "sb-sell" : "sb-neutral";

  const statusText =
    status === "Target1 Hit" ? "TARGET 1 BREACHED"
    : status === "Target2 Hit" ? "TARGET 2 BREACHED"
    : status === "Target3 Hit" ? "TARGET 3 BREACHED"
    : status === "Stop Loss Hit" ? "STOP LOSS HIT"
    : status === "Trailing SL Hit" ? "TRAILING SL HIT"
    : status === "Active" ? "TRADE ACTIVE"
    : "SCANNING…";

  const statusMod =
    status === "Active" || status === "Waiting" ? ""
    : status.includes("Target") ? " sb-status--tgt"
    : status === "Stop Loss Hit" ? " sb-status--sl"
    : " sb-status--tsl";

  // Ladder rows follow actual price order: for SELL, stop/trail sit above the
  // entry and targets sit below it (mirrors BUY, where targets sit above and
  // stop/trail sit below), so the vertical rail always reads high→low.
  const targetRows: { key: string; label: string; value: number | null; kind: "tgt" | "entry" | "sl" | "tsl"; hit: boolean }[] = [
    { key: "t3", label: "TGT 3", value: target3, kind: "tgt", hit: tp3Hit },
    { key: "t2", label: "TGT 2", value: target2, kind: "tgt", hit: tp2Hit },
    { key: "t1", label: "TGT 1", value: target1, kind: "tgt", hit: tp1Hit },
  ];
  const entryRow = { key: "entry", label: "ENTRY", value: entry, kind: "entry" as const, hit: false };
  const stopRows: { key: string; label: string; value: number | null; kind: "tgt" | "entry" | "sl" | "tsl"; hit: boolean }[] = [
    { key: "tsl", label: "TRAIL", value: trailingStop, kind: "tsl", hit: tslHit },
    { key: "sl", label: "STOP", value: stopLoss, kind: "sl", hit: slHit },
  ];
  const ladder = isShort
    ? [...stopRows.slice().reverse(), entryRow, ...targetRows.slice().reverse()]
    : [...targetRows, entryRow, ...stopRows];

  return (
    <>
      {/* Arrow Badges */}
      {arrowBadges.map((badge, i) => (
        <div
          key={i}
          className={`sb-arrow-badge sb-arrow-badge--${badge.direction}`}
          style={{
            left: badge.x,
            top: badge.direction === "long" ? badge.y + 30 : badge.y - 44,
          }}
        >
          {badge.direction === "long" ? "Buy" : "Sell"}
        </div>
      ))}

      <div className={`sb-overlay ${themeClass}${showDashboard ? "" : " sb-overlay--collapsed"}`}>
        <div className="sb-edge-glow" />

        {/* Header */}
        <div className="sb-header">
          <span className="sb-brand-icon">⟁</span>
          <span className="sb-title">SHADOW BREAKER</span>
          <button
            className="sb-toggle-btn"
            onClick={onToggleDashboard}
            title={showDashboard ? "Hide dashboard" : "Show dashboard"}
            aria-label={showDashboard ? "Hide dashboard" : "Show dashboard"}
          >
            <span className={`sb-toggle-icon${showDashboard ? " sb-toggle-icon--open" : ""}`}>&#x25BE;</span>
          </button>
        </div>

        {showDashboard && (
          <>
            {/* Symbol + Price */}
            <div className="sb-quote-row">
              <span className="sb-symbol">{symbol}</span>
              <span className="sb-cmp">{currentPrice.toFixed(2)}</span>
            </div>

            {/* Signal ring badge */}
            <div className="sb-signal-wrap">
              <div className={`sb-signal-ring${isActive ? " sb-signal-ring--pulse" : ""}`}>
                <span className="sb-signal-arrow">{isLong ? "▲" : isShort ? "▼" : "•"}</span>
                <span className="sb-signal-label">{signal ?? "IDLE"}</span>
              </div>
              <div className="sb-pl-split">
                <div className="sb-pl-cell">
                  <span className="sb-pl-lbl">LIVE</span>
                  <span className={`sb-pl-val ${livePL >= 0 ? "sb-profit" : "sb-loss"}`}>{fmtPL(livePL)}</span>
                </div>
                <div className="sb-pl-cell">
                  <span className="sb-pl-lbl">BOOKED</span>
                  <span className={`sb-pl-val ${bookedPL >= 0 ? "sb-profit" : "sb-loss"}`}>{fmtPL(bookedPL)}</span>
                </div>
              </div>
            </div>

            {/* Price ladder — vertical rail with node markers */}
            <div className="sb-ladder">
              <div className="sb-ladder-rail" />
              {ladder.map(row => (
                <div className={`sb-ladder-row sb-ladder-row--${row.kind}${row.hit ? " sb-ladder-row--hit" : ""}`} key={row.key}>
                  <span className={`sb-ladder-node sb-ladder-node--${row.kind}${row.hit ? " sb-ladder-node--hit" : ""}`} />
                  <span className="sb-ladder-label">{row.label}</span>
                  <span className="sb-ladder-value">
                    {fmt(row.value)}
                    {row.hit && <span className="sb-ladder-check">{row.kind === "tgt" ? "✓" : "✗"}</span>}
                  </span>
                </div>
              ))}
            </div>

            {/* Status ribbon */}
            <div className={`sb-status${statusMod}`}>
              <span className="sb-status-text">{statusText}</span>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ShadowBreakerOverlay;
